import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

/**
 * What the user is looking at.
 *
 * The panel's job here is to remove a sentence from every question. Standing in an
 * Objektakte, "was ist hier auffällig?" has to work without naming the property, so
 * the route is resolved to a screen name and, where the route addresses one record,
 * to that record's identity.
 *
 * The record's *name* is read from the page's own heading rather than fetched again.
 * That is not a shortcut: the heading is what the user is looking at, so if the panel
 * says the same words, the two cannot disagree — and no second request is made for a
 * string already on screen.
 */

export interface Bildschirm {
  /** Path prefix. Ordered longest-first so the detail route wins over the list. */
  pfad: string;
  label: string;
  /** True when `:id` follows the prefix. */
  detail?: boolean;
}

const BILDSCHIRME: Bildschirm[] = [
  { pfad: "/liegenschaften/", label: "Objektakte", detail: true },
  { pfad: "/schaeden/", label: "Schadensfall", detail: true },
  { pfad: "/liegenschaften", label: "Liegenschaften" },
  { pfad: "/bestandverhaeltnisse", label: "Bestandverhältnisse" },
  { pfad: "/personen", label: "Personen" },
  { pfad: "/vorschreibungen", label: "Vorschreibungen" },
  { pfad: "/eingangsrechnungen", label: "Eingangsrechnungen" },
  { pfad: "/buchhaltung", label: "Buchhaltung" },
  { pfad: "/offene-posten", label: "Offene Posten" },
  { pfad: "/mahnwesen", label: "Mahnwesen" },
  { pfad: "/abrechnungen", label: "Abrechnungen" },
  { pfad: "/bank", label: "Bank und Zahlungen" },
  { pfad: "/schaeden", label: "Schäden" },
  { pfad: "/auftraege", label: "Aufträge und Vergabe" },
  { pfad: "/fristen", label: "Fristen" },
  { pfad: "/dokumente", label: "Dokumente" },
  { pfad: "/auswertungen", label: "Auswertungen" },
  { pfad: "/verwaltung", label: "Organisation" },
];

export interface Arbeitskontext {
  /** German name of the screen, e.g. "Objektakte". */
  bildschirm: string;
  /** The record's own heading, when the screen shows one record. */
  satz: string | null;
  liegenschaftId: string | null;
  schadenId: string | null;
  periode: string | null;
  /** One line for the context strip and for the stored transcript. */
  label: string;
}

export function useArbeitskontext(): Arbeitskontext {
  const ort = useLocation();
  const params = useParams();
  const ueberschrift = useSeitenUeberschrift(ort.pathname);

  return useMemo(() => {
    const treffer = BILDSCHIRME.find((b) =>
      b.detail ? ort.pathname.startsWith(b.pfad) : ort.pathname === b.pfad,
    );
    const bildschirm = treffer?.label ?? (ort.pathname === "/" ? "Periodenlauf" : "Kontor");

    const id = params.id ?? null;
    const liegenschaftId = ort.pathname.startsWith("/liegenschaften/") ? id : null;
    const schadenId = ort.pathname.startsWith("/schaeden/") ? id : null;

    const periode = new URLSearchParams(ort.search).get("periode");

    const teile = [bildschirm];
    if (ueberschrift && ueberschrift !== bildschirm) teile.push(ueberschrift);
    if (periode) teile.push(periode);

    return {
      bildschirm,
      satz: ueberschrift,
      liegenschaftId,
      schadenId,
      periode: periode && /^\d{4}-\d{2}$/.test(periode) ? periode : null,
      label: teile.join(" · "),
    };
  }, [ort.pathname, ort.search, params.id, ueberschrift]);
}

/**
 * The work surface's own `h1`, kept current.
 *
 * A MutationObserver rather than a one-shot read, because the heading appears after
 * the record loads — a read at navigation time would show the previous property's name
 * for as long as the fetch takes, which is precisely the moment the user asks
 * "was ist hier auffällig?".
 */
function useSeitenUeberschrift(pfad: string): string | null {
  const [ueberschrift, setUeberschrift] = useState<string | null>(null);

  useEffect(() => {
    const flaeche = document.querySelector("main");
    if (!flaeche) return;

    const lies = (): void => {
      const text = flaeche.querySelector("h1")?.textContent?.trim();
      setUeberschrift(text && text.length <= 90 ? text : null);
    };

    lies();
    const beobachter = new MutationObserver(lies);
    beobachter.observe(flaeche, { childList: true, subtree: true, characterData: true });
    return () => beobachter.disconnect();
  }, [pfad]);

  return ueberschrift;
}
